import { useState, useMemo } from 'react';
import { X, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { MESSAGING_CONDITION_TYPES } from '@/types/field-mapping-flow';

interface ConditionConfigProps {
  docId: string;
  onClose: () => void;
}

/**
 * Condition editor panel for messaging app branches.
 * Allows editing the branch name and condition (filterValue).
 */
export function ConditionConfig({ docId, onClose }: ConditionConfigProps) {
  const fieldMappings = useFlowBuilderStore((s) => s.fieldMappings);
  const updateBranchName = useFlowBuilderStore((s) => s.updateBranchName);
  const updateBranchCondition = useFlowBuilderStore((s) => s.updateBranchCondition);
  const deleteBranch = useFlowBuilderStore((s) => s.deleteBranch);
  const saveBranch = useFlowBuilderStore((s) => s.saveBranch);
  const saving = useFlowBuilderStore((s) => s.saving);

  const mapping = fieldMappings[docId];

  // Derive initial values — these must come before any conditional return
  const initialConditionType = useMemo(() => {
    if (!mapping) return '';
    const condType = MESSAGING_CONDITION_TYPES.find((t) =>
      mapping.filterValue.startsWith(t.prefix)
    );
    return condType?.value || '';
  }, [mapping]);

  const initialConditionValue = useMemo(() => {
    if (!mapping) return '';
    const condType = MESSAGING_CONDITION_TYPES.find((t) =>
      mapping.filterValue.startsWith(t.prefix)
    );
    if (condType) {
      return mapping.filterValue.replace(condType.prefix, '');
    }
    return mapping.filterValue === '---default' ? '' : mapping.filterValue;
  }, [mapping]);

  const [name, setName] = useState(mapping?.name || '');
  const [conditionType, setConditionType] = useState(initialConditionType);
  const [conditionValue, setConditionValue] = useState(initialConditionValue);

  // Now safe to do conditional return — all hooks are above
  if (!mapping) return null;

  const isDefault = mapping.filterValue === '---default';
  const isSaving = saving[docId];

  const handleSave = () => {
    if (name.trim()) {
      updateBranchName(docId, name.trim());
    }

    if (!isDefault && conditionType && conditionValue) {
      const condType = MESSAGING_CONDITION_TYPES.find((t) => t.value === conditionType);
      if (condType) {
        const filterValue = `${condType.prefix}${conditionValue}`;
        const filterLabel = `${condType.label}: ${conditionValue}`;
        updateBranchCondition(docId, filterValue, filterLabel);
      }
    }

    saveBranch(docId);
  };

  const handleDelete = async () => {
    if (window.confirm(`Delete "${mapping.name || 'this branch'}"? This cannot be undone.`)) {
      await deleteBranch(docId);
      onClose();
    }
  };

  return (
    <div className="absolute left-4 top-4 z-50 w-[320px] rounded-xl border border-border bg-background shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <h3 className="text-sm font-semibold text-foreground">
          {isDefault ? 'Default Branch' : 'Edit Condition'}
        </h3>
        <Button variant="ghost" size="icon-sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 space-y-3">
        {/* Name */}
        <div>
          <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
            Branch Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter name..."
            className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Condition (not for default branch) */}
        {!isDefault && (
          <>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Condition Type
              </label>
              <select
                value={conditionType}
                onChange={(e) => setConditionType(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Select type...</option>
                {MESSAGING_CONDITION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {conditionType && (
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                  Value
                </label>
                <input
                  type="text"
                  value={conditionValue}
                  onChange={(e) => setConditionValue(e.target.value)}
                  placeholder="Enter value..."
                  className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            )}
          </>
        )}

        {isDefault && (
          <p className="text-[11px] text-muted-foreground">
            This branch runs when no other condition matches. It&apos;s always the lowest
            priority fallback.
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border px-4 h-[45px]">
        {!isDefault ? (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-xs text-destructive hover:text-destructive"
            onClick={handleDelete}
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </Button>
        ) : (
          <div />
        )}
        <Button size="sm" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
